import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { OpenStatus } from './OpenStatus';
import { heroFacts, images, shop } from '../data/shop';
export type HeroTheme = 'charcoal' | 'plum' | 'steel' | 'light';
type HeroProps = {
  theme?: HeroTheme;
};
type ThemeClasses = {
  section: string;
  eyebrow: string;
  body: string;
  primary: string;
  secondary: string;
  divider: string;
  factValue: string;
  factLabel: string;
};
const darkBase = {
  eyebrow: 'text-white/65',
  body: 'text-white/75',
  secondary: 'border-white/25 text-white hover:border-white/50 hover:bg-white/5 focus-visible:ring-white',
  divider: 'border-white/15',
  factValue: 'text-white',
  factLabel: 'text-white/60'
};
const darkPrimary = 'bg-surface text-accent hover:bg-paper focus-visible:ring-white focus-visible:ring-offset-2';
const themes: Record<HeroTheme, ThemeClasses> = {
  charcoal: {
    ...darkBase,
    section: 'bg-charcoal text-white',
    primary: `${darkPrimary} focus-visible:ring-offset-charcoal`
  },
  plum: {
    ...darkBase,
    section: 'bg-accent-strong text-white',
    primary: `${darkPrimary} focus-visible:ring-offset-accent-strong`
  },
  steel: {
    ...darkBase,
    section: 'bg-steel text-white',
    primary: `${darkPrimary} focus-visible:ring-offset-steel`
  },
  light: {
    section: 'bg-paper text-ink',
    eyebrow: 'text-muted',
    body: 'text-muted',
    primary: 'bg-accent text-white hover:bg-accent-strong focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-paper',
    secondary: 'border-line text-ink hover:border-ink/40 hover:bg-surface focus-visible:ring-accent',
    divider: 'border-line',
    factValue: 'text-accent',
    factLabel: 'text-muted'
  }
};
export function Hero({
  theme = 'charcoal'
}: HeroProps) {
  const t = themes[theme];
  return <section id="top" className={`relative ${t.section}`} data-id="element-164" data-mp="2ll0fgz4n04ek">
      <div className="mx-auto grid max-w-7xl px-6 lg:min-h-[640px] lg:grid-cols-12 lg:items-center lg:px-8" data-id="element-165" data-mp="2lml98mj4njbd">
        <motion.div className="py-16 lg:col-span-6 lg:py-24 lg:pr-12" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-166" data-mp="nbb9spq0zv12">
          <p className={`text-sm font-medium ${t.eyebrow}`} data-id="element-167" data-mp="degfkj0scgsl">Locally owned auto body shop · Helena, Montana since 1948</p>
          <h1 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-[3.75rem]" data-id="element-168" data-mp="1b04bz3x8qz44">
            Collision repair and paint, done right the first time.
          </h1>
          <p className={`mt-6 max-w-xl text-lg leading-relaxed ${t.body}`} data-id="element-169" data-mp="110wr7vo6g26z">
            We repair every make and model, work directly with your insurance company, and stand behind every
            job — so getting your car fixed is the easy part of a bad day.
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-170" data-mp="3hqkckmtau0vh">
            <a href="#estimate" className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-6 py-3.5 text-base font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 ${t.primary}`} data-id="element-171" data-mp="1x9umefegzlpj">
              Get a free estimate
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-172" data-mp="2b1hjuik5f09w" />
            </a>
            <a href={shop.phoneHref} className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border px-6 py-3.5 text-base font-semibold transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 ${t.secondary}`} data-id="element-173" data-mp="2g0lqp2jlolx2">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-174" data-mp="2tyiscdcwviil" />
              {shop.phone}
            </a>
          </div>

          <dl className={`mt-12 grid max-w-xl grid-cols-3 gap-6 border-t pt-6 ${t.divider}`} data-id="element-175" data-mp="uo0ws9w3j8tt">
            {heroFacts.map(fact => <div key={fact.value} data-id="element-176" data-mp="4bdx7gy3ybp5">
                <dt className={`font-display text-lg font-bold ${t.factValue}`} data-id="element-177" data-mp="4acnbfstvv90">{fact.value}</dt>
                <dd className={`mt-1 text-sm leading-snug ${t.factLabel}`} data-id="element-178" data-mp="3vgnhqt3c1xrd">{fact.label}</dd>
              </div>)}
          </dl>
        </motion.div>
      </div>

      <div className="relative lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2" data-id="element-179" data-mp="9aqku3wszmss">
          <img src={images.hero} alt="Technician spraying red paint on a car in Helena Body and Paint's downdraft paint booth" className="aspect-[4/3] h-full w-full object-cover lg:aspect-auto" data-id="element-180" data-mp="98mk1uu7b3wq" />
          <div className="absolute bottom-6 left-6 right-6 flex flex-col gap-1.5 rounded-md bg-surface px-5 py-4 text-ink shadow-lg sm:left-auto lg:bottom-8 lg:right-8" data-id="element-181" data-mp="3rh58iukvjb4d">
            <OpenStatus tone="dark" data-id="element-182" data-mp="2yzj35wxu96rc" />
            <a href={shop.mapsHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink" data-id="element-183" data-mp="2yyhshtkqima5">
              <MapPinIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-184" data-mp="19hcge67k5ram" />
              {shop.addressLine1}, {shop.addressLine2}
            </a>
          </div>
      </div>
    </section>;
}